import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Smartphone,
  Apple,
  Play,
  Download,
  Star,
  Check,
  Wifi,
  WifiOff,
  Bell,
  Shield,
  Sparkles,
  Volume2,
  Moon,
  Heart,
  ArrowRight
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";

const appFeatures = [
  {
    icon: Sparkles,
    title: "AI Story Generation",
    description: "Create unique, personalized bedtime stories in seconds"
  },
  {
    icon: Volume2,
    title: "Professional Narration",
    description: "High-quality text-to-speech with natural voices"
  },
  {
    icon: Moon,
    title: "Sleep Timer",
    description: "Auto-stop after the story ends for peaceful sleep"
  },
  {
    icon: WifiOff,
    title: "Offline Mode",
    description: "Download stories to read anywhere, anytime"
  },
  {
    icon: Bell,
    title: "Bedtime Reminders",
    description: "Gentle notifications to start your routine"
  },
  {
    icon: Shield,
    title: "Safe & Ad-Free",
    description: "COPPA-compliant with no advertisements"
  }
];

const reviews = [
  {
    name: "Sarah M.",
    rating: 5,
    text: "This app has transformed our bedtime routine! My daughter loves hearing stories with her name in them.",
    platform: "iOS"
  },
  {
    name: "Mike D.",
    rating: 5,
    text: "Finally, an app that my kids actually want to use for bedtime. The personalized stories are amazing!",
    platform: "Android"
  },
  {
    name: "Jennifer L.",
    rating: 5,
    text: "The narration quality is fantastic. It's like having a professional storyteller at home every night.",
    platform: "iOS"
  }
];

const stats = [
  { value: "4.8", label: "App Store Rating" },
  { value: "100K+", label: "Downloads" },
  { value: "500K+", label: "Stories Created" },
  { value: "50K+", label: "Happy Families" }
];

export default function AppPage() {
  useSEO(SEO_CONFIGS.app);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-violet-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 mb-6">
                <Smartphone className="h-4 w-4" />
                <span className="text-sm font-medium">Available on iOS & Android</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Magical Stories in Your Pocket
              </h1>

              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Download the SnuggleCrafters app and create personalized AI bedtime stories anywhere, anytime.
                Perfect for home, travel, or whenever your little ones need a magical tale.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Button size="lg" className="gap-2 bg-black hover:bg-gray-800 text-white">
                  <Apple className="h-5 w-5" />
                  Download for iOS
                </Button>
                <Button size="lg" className="gap-2 bg-green-600 hover:bg-green-700 text-white">
                  <Play className="h-5 w-5" />
                  Get on Android
                </Button>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-600 dark:text-gray-300">4.8 rating from 1,250+ reviews</span>
              </div>
            </div>

            <div className="relative">
              <div className="relative mx-auto w-64 md:w-80">
                {/* Phone mockup */}
                <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-[3rem] p-3 shadow-2xl">
                  <div className="bg-gray-900 rounded-[2.5rem] p-2">
                    <div className="bg-gradient-to-br from-violet-100 to-pink-100 dark:from-violet-900 dark:to-pink-900 rounded-[2rem] aspect-[9/19.5] flex items-center justify-center">
                      <div className="text-center p-6">
                        <Sparkles className="h-16 w-16 mx-auto text-purple-600 mb-4" />
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">SnuggleCrafters</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">AI Bedtime Stories</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 bg-white dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 dark:text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything You Need for Bedtime</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              The SnuggleCrafters app is packed with features designed to make bedtime magical.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get Started in Minutes</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Creating personalized bedtime stories has never been easier.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Download", description: "Get the app from your device's app store" },
              { step: "2", title: "Create Profile", description: "Add your child's name, age, and interests" },
              { step: "3", title: "Generate Story", description: "Let AI create a personalized tale" },
              { step: "4", title: "Enjoy", description: "Read or listen to the magical story" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Loved by Families</h2>
            <p className="text-gray-600 dark:text-gray-300">
              See what parents are saying about SnuggleCrafters.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4 italic">"{review.text}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{review.name}</span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      {review.platform === "iOS" ? <Apple className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      {review.platform}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Download className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Bedtime?</h2>
          <p className="text-xl opacity-90 mb-8">
            Download SnuggleCrafters today and start creating magical stories for your family.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="gap-2 bg-black hover:bg-gray-800 text-white">
              <Apple className="h-5 w-5" />
              Download for iOS
            </Button>
            <Button size="lg" className="gap-2 bg-white text-purple-600 hover:bg-gray-100">
              <Play className="h-5 w-5" />
              Get on Android
            </Button>
          </div>

          <p className="mt-8 text-sm opacity-75">
            Also available on web at snugglecrafters.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
